import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "./ui/button";
import { X, Download, Loader2, Share2, BookOpen, Film, Tv, Gamepad2, User, Star, Link2, Award } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "../i18n";
import { toPng } from "html-to-image";

const API = '/api';

const LOGO_SVG = "/favicon.svg";

const MEDIA_ICONS = { book: BookOpen, movie: Film, tv_series: Tv, video_game: Gamepad2 };
const MILESTONE_LABELS_ES = { book: "LIBRO COMPLETADO", movie: "PELÍCULA VISTA", tv_series: "SERIE TERMINADA", video_game: "JUEGO COMPLETADO" };
const MILESTONE_LABELS_EN = { book: "BOOK COMPLETED", movie: "FILM WATCHED", tv_series: "SERIES FINISHED", video_game: "GAME COMPLETED" };
const STARTED_LABELS_ES = { book: "LEYENDO", movie: "VIENDO PELÍCULA", tv_series: "VIENDO SERIE", video_game: "JUGANDO" };
const STARTED_LABELS_EN = { book: "NOW READING", movie: "NOW WATCHING", tv_series: "NOW WATCHING", video_game: "NOW PLAYING" };
const TYPE_LABELS_ES = { book: "Libro", movie: "Película", tv_series: "Serie", video_game: "Juego" };
const TYPE_LABELS_EN = { book: "Book", movie: "Movie", tv_series: "Series", video_game: "Game" };

/* ─── Content Card (Book/Film/Series/Game) ─── */
function ContentCard({ username, title, coverUrl, rating, mediaType, genre, creator, type, lang }) {
  const isCompleted = type === "completed" || type === "dropped";
  const milestoneLabels = isCompleted ? (lang === "en" ? MILESTONE_LABELS_EN : MILESTONE_LABELS_ES) : (lang === "en" ? STARTED_LABELS_EN : STARTED_LABELS_ES);
  const typeLabels = lang === "en" ? TYPE_LABELS_EN : TYPE_LABELS_ES;
  const milestoneLabel = milestoneLabels[mediaType] || (lang === "en" ? "COMPLETED" : "COMPLETADO");
  const TypeIcon = MEDIA_ICONS[mediaType] || BookOpen;
  const dateStr = new Date().toLocaleDateString(lang === "en" ? "en-US" : "es-ES", { month: "long", year: "numeric" });
  const fullStars = rating ? Math.floor(rating) : 0;
  const hasHalf = rating ? rating % 1 >= 0.5 : false;

  return (
    <div style={{ width: 390, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", overflow: "hidden", borderRadius: 16, border: "1px solid #2a2a2a", background: "#0f0f0f" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", borderBottom: "1px solid #2a2a2a", height: 38 }}>
        <span style={{ fontSize: 12, color: "#888" }}>@{username}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <img src={LOGO_SVG} alt="" style={{ height: 18, width: "auto" }} onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "inline"; }} />
          <span style={{ display: "none", fontSize: 12, fontWeight: 700, color: "#4db8a4", letterSpacing: "0.05em" }}>ADVENTURE TRACK!</span>
        </div>
      </div>

      {/* Body: two columns */}
      <div style={{ display: "flex" }}>
        {/* Left: cover */}
        <div style={{ width: 110, minHeight: 150, flexShrink: 0, position: "relative", overflow: "hidden" }}>
          {coverUrl ? (
            <img src={`${API}/proxy/image?url=${encodeURIComponent(coverUrl)}`} alt="" style={{ width: 110, height: "100%", objectFit: "cover", display: "block", minHeight: 150 }} crossOrigin="anonymous" />
          ) : (
            <div style={{ width: 110, minHeight: 150, background: "#1a6b5a", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", position: "relative" }}>
              <TypeIcon style={{ width: 32, height: 32, color: "#0d3d33" }} />
              <span style={{ fontSize: 9, fontWeight: 700, color: "#fff", textTransform: "uppercase", textAlign: "center", marginTop: 8, padding: "0 8px", lineHeight: 1.3, letterSpacing: "0.05em" }}>{title}</span>
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 28, background: "#0d3d33", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{ fontSize: 8, fontWeight: 600, color: "#4db8a4", textTransform: "uppercase", letterSpacing: "0.08em" }}>{genre || typeLabels[mediaType] || ""}</span>
              </div>
            </div>
          )}
        </div>

        {/* Right: info */}
        <div style={{ flex: 1, padding: 14, display: "flex", flexDirection: "column", gap: 6, minHeight: 150 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: "#4db8a4", letterSpacing: "0.1em", textTransform: "uppercase" }}>{milestoneLabel}</span>
          <span style={{ fontSize: 14, fontWeight: 500, color: "#f0f0f0", lineHeight: 1.3 }}>{title}</span>

          {/* Stars */}
          {rating > 0 && (
            <div style={{ display: "flex", gap: 2, marginTop: 2 }}>
              {[1, 2, 3, 4, 5].map(i => (
                <span key={i} style={{ fontSize: 14, color: i <= fullStars || (i === fullStars + 1 && hasHalf) ? "#f5a623" : "#333" }}>★</span>
              ))}
            </div>
          )}

          {/* Creator */}
          {creator && (
            <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 2 }}>
              <User style={{ width: 11, height: 11, color: "#666" }} />
              <span style={{ fontSize: 11, color: "#888" }}>{creator}</span>
            </div>
          )}

          {/* Pills */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 2 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10, color: "#888", background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 99, padding: "2px 8px" }}>
              <TypeIcon style={{ width: 10, height: 10 }} />
              {typeLabels[mediaType] || mediaType}
            </span>
            {genre && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10, color: "#888", background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 99, padding: "2px 8px" }}>
                {genre}
              </span>
            )}
          </div>

          {/* Date pinned to bottom */}
          <span style={{ fontSize: 10, color: "#555", marginTop: "auto" }}>
            {isCompleted ? (lang === "en" ? "Completed" : "Completado") : (lang === "en" ? "Started" : "Empezado")} · {dateStr}
          </span>
        </div>
      </div>

      {/* Footer */}
      <CardFooter />
    </div>
  );
}

/* ─── Achievement Card ─── */
function AchievementCard({ username, achievementName, achievementDescription, achievementIcon, achievementImage, totalEarned, totalAchievements, lang }) {
  const progressPercent = totalAchievements > 0 ? (totalEarned / totalAchievements) * 100 : 0;

  return (
    <div style={{ width: 390, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", overflow: "hidden", borderRadius: 16, border: "1px solid #2a2a2a", background: "#0f0f0f" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", borderBottom: "1px solid #2a2a2a", height: 38 }}>
        <span style={{ fontSize: 12, color: "#888" }}>@{username}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <img src={LOGO_SVG} alt="" style={{ height: 18, width: "auto" }} onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "inline"; }} />
          <span style={{ display: "none", fontSize: 12, fontWeight: 700, color: "#4db8a4", letterSpacing: "0.05em" }}>ADVENTURE TRACK!</span>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: 14 }}>
        <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
          {/* Icon container */}
          <div style={{ width: 64, height: 64, borderRadius: 12, background: "#2a2010", border: "1px solid #5a4520", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            {achievementImage ? (
              <img src={achievementImage} alt="" style={{ maxWidth: 36, maxHeight: 36, objectFit: "contain" }} />
            ) : (
              <Star style={{ width: 30, height: 30, color: "#f5a623" }} fill="#f5a623" />
            )}
          </div>

          {/* Info */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: "#4db8a4", letterSpacing: "0.1em", textTransform: "uppercase" }}>
              {lang === "en" ? "ACHIEVEMENT UNLOCKED" : "LOGRO DESBLOQUEADO"}
            </span>
            <span style={{ fontSize: 15, fontWeight: 500, color: "#f0f0f0", lineHeight: 1.25 }}>{achievementName}</span>
            {achievementDescription && (
              <span style={{ fontSize: 11, color: "#888", lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{achievementDescription}</span>
            )}
            <div style={{ display: "flex", gap: 6, marginTop: 2 }}>
              <span style={{ fontSize: 10, color: "#8b6914", background: "#2a2010", border: "1px solid #5a4520", borderRadius: 99, padding: "2px 8px" }}>
                {lang === "en" ? "Rare" : "Poco común"}
              </span>
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ marginTop: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontSize: 10, color: "#555" }}>{lang === "en" ? "Achievement progress" : "Progreso logros"}</span>
            <span style={{ fontSize: 10, color: "#555" }}>{totalEarned} / {totalAchievements}</span>
          </div>
          <div style={{ height: 3, background: "#1a1a1a", borderRadius: 99, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${progressPercent}%`, background: "#4db8a4", borderRadius: 99 }} />
          </div>
        </div>
      </div>

      {/* Footer */}
      <CardFooter />
    </div>
  );
}

/* ─── Shared Footer ─── */
function CardFooter() {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 14px", borderTop: "1px solid #2a2a2a", height: 32 }}>
      <span style={{ fontSize: 10, color: "#444" }}>adventuretrack.online</span>
      <div style={{ display: "flex", gap: 8 }}>
        <Link2 style={{ width: 13, height: 13, color: "#333" }} />
        <Gamepad2 style={{ width: 13, height: 13, color: "#333" }} />
        <Tv style={{ width: 13, height: 13, color: "#333" }} />
        <BookOpen style={{ width: 13, height: 13, color: "#333" }} />
      </div>
    </div>
  );
}

/* ─── Modal ─── */
export function SocialShareModal({
  isOpen, onClose, type, username, title, coverUrl, rating,
  achievementName, achievementIcon, achievementDescription, achievementImage,
  mediaType, genre, creator, totalEarned, totalAchievements,
}) {
  const { t, lang } = useTranslation();
  const cardRef = useRef(null);
  const [generating, setGenerating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const isAchievement = type === "achievement";

  const generate = useCallback(async () => {
    if (!cardRef.current) return;
    setGenerating(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: "#0f0f0f",
      });
      setPreviewUrl(dataUrl);
    } catch (err) {
      console.error("Error generating share card:", err);
    } finally {
      setGenerating(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setPreviewUrl(null);
      const timer = setTimeout(generate, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen, generate]);

  const handleDownload = () => {
    if (!previewUrl) return;
    const a = document.createElement("a");
    a.href = previewUrl;
    a.download = `adventuretrack_${(title || achievementName || "share").replace(/[^a-zA-Z0-9]/g, "_").slice(0, 40)}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleNativeShare = async () => {
    if (!previewUrl) return;
    try {
      const res = await fetch(previewUrl);
      const blob = await res.blob();
      const file = new File([blob], "adventuretrack.png", { type: "image/png" });
      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: "Adventure Track!" });
        return;
      }
    } catch { /* fallback to download */ }
    handleDownload();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm"
        onClick={onClose} data-testid="social-share-modal-overlay"
      >
        <motion.div
          initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.2 }}
          className="bg-background border border-border rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[90vh] flex flex-col overflow-hidden"
          onClick={(e) => e.stopPropagation()} data-testid="social-share-modal"
        >
          <div className="flex items-center justify-between p-4 border-b border-border">
            <h3 className="font-semibold text-base">{t.share.imageForShare}</h3>
            <Button variant="ghost" size="icon" className="w-8 h-8" onClick={onClose} data-testid="social-share-close">
              <X className="w-4 h-4" />
            </Button>
          </div>
          <div className="p-4 flex-1 overflow-y-auto space-y-4">
            {/* Hidden card for rendering */}
            <div style={{ position: "absolute", left: -9999, top: -9999 }}>
              <div ref={cardRef}>
                {isAchievement ? (
                  <AchievementCard
                    username={username}
                    achievementName={achievementName}
                    achievementDescription={achievementDescription}
                    achievementImage={achievementImage}
                    totalEarned={totalEarned || 0}
                    totalAchievements={totalAchievements || 0}
                    lang={lang}
                  />
                ) : (
                  <ContentCard
                    username={username}
                    title={title}
                    coverUrl={coverUrl}
                    rating={rating}
                    mediaType={mediaType}
                    genre={genre}
                    creator={creator}
                    type={type}
                    lang={lang}
                  />
                )}
              </div>
            </div>

            {/* Preview */}
            <div className="relative w-full rounded-xl overflow-hidden border border-border bg-muted" data-testid="social-share-preview">
              {generating || !previewUrl ? (
                <div className="flex flex-col items-center justify-center gap-3 py-20">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Generando imagen...</p>
                </div>
              ) : (
                <img src={previewUrl} alt="Preview" className="w-full h-auto" data-testid="social-share-image" />
              )}
            </div>

            <p className="text-xs text-center text-muted-foreground">{t.share.downloadHelper}</p>

            <div className="flex gap-3">
              <Button className="flex-1" onClick={handleNativeShare} disabled={!previewUrl || generating} data-testid="social-share-download">
                <Download className="w-4 h-4 mr-2" />{t.share.downloadImage}
              </Button>
              <Button variant="outline" onClick={onClose} data-testid="social-share-cancel">{t.common.close}</Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
